import {zodResolver} from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';

import {AlertInfo, AlertModal, MaskedInput} from 'presentation/components';
import {AlertActions} from 'presentation/components/AlertActions';
import {Loading} from 'presentation/components/Loading/Loading';
import {useHostName} from 'presentation/hooks';
import {TermsOfUseAndPrivacyPolicy} from 'presentation/pages/public/components/TermsOfUseAndPrivacyPolicy';
import {useForgotPasswordRecoverUserInfo} from 'presentation/pages/public/hooks/useForgotPasswordRecoverUserInfo';
import {useSteps} from 'presentation/pages/public/hooks/useSteps';
import {StyledFixedFooter} from 'presentation/pages/public/styles';
import {masks} from 'presentation/utils';

import {HttpStatusCode} from 'data/protocols/http';
import {SignInEnum} from 'presentation/constants';
import {PublicFlowEnum} from 'presentation/pages/public/constants/PublicFlowEnum';
import {LOCAL_STORAGE_KEYS} from '../../../constants/localStorageKeys';
import {CompanionAlert} from '../../CompanionAlert';
import {formSchema, IFormData} from './schema';

const {formLabels, actionText, formPlaceholders} = translations['pt-br'].publicFlow;
const {userAndDateOfBirth, invalidData, noDataModal} =
	translations['pt-br'].publicFlow.forgotPassword;
const {serverError, unexpectedErrorWarning, serverErrorTitle, tryAgain} =
	translations['pt-br'].commons;

const {alert} = translations['pt-br'].publicFlow.forgotPassword;

export function Step1Form() {
	const {isGndiSaoPaulo} = useHostName();
	const {state} = useLocation();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const {handleHelpStep} = useSteps(PublicFlowEnum.FORGOT_PASSWORD);
	const navigate = useNavigate();

	const isMaxLoginAttemptsRedirect = state?.maxLoginAttempts;

	const placeholder = isGndiSaoPaulo ? formPlaceholders.signInCPF : formPlaceholders.signIn;
	const signInLabel = isGndiSaoPaulo ? formLabels.cpf : formLabels.user;

	const methods = useForm<IFormData>({
		defaultValues: {
			login: '',
			birthday: undefined,
		},
		resolver: zodResolver(formSchema),
		mode: 'onSubmit',
	});

	const {
		handleSubmit,
		register,
		formState: {errors, isSubmitting},
	} = methods;
	const {
		handleCheckPersonalData,
		showAPIError,
		errorStatusCode,
		clearAPIError,
		showNoContactModal,
	} = useForgotPasswordRecoverUserInfo({
		methods,
	});

	useEffect(() => {
		if (showAPIError && errorStatusCode !== HttpStatusCode.unprocessable) {
			setIsModalOpen(true);
		} else {
			setIsModalOpen(false);
		}
	}, [showAPIError, errorStatusCode]);

	return (
		<Fragment>
			<FormProvider {...methods}>
				<Stack component="form" gap={2} onSubmit={handleSubmit(handleCheckPersonalData)}>
					{isMaxLoginAttemptsRedirect ? (
						<AlertInfo.Root severity="warning">
							<AlertInfo.Title title={alert.heading} />
							<AlertInfo.Description>{alert.description}</AlertInfo.Description>
						</AlertInfo.Root>
					) : null}
					<Typography>{userAndDateOfBirth}</Typography>

					<Stack>
						<Typography variant="label" component="label" htmlFor="login">
							{signInLabel}
						</Typography>
						<MaskedInput
							{...register('login')}
							id="login"
							maskOptions={masks.login}
							autoComplete="current-login"
							error={!!errors.login}
							helperText={errors.login?.message}
							label={placeholder}
							$fieldName="login"
						/>
					</Stack>
					<Stack>
						<Typography variant="label" component="label" htmlFor="birthday">
							{formLabels.birthday}
						</Typography>
						<MaskedInput
							{...register('birthday')}
							id="birthday"
							maskOptions={masks.birthday}
							autoComplete="current-birthday"
							error={!!errors.birthday}
							helperText={errors.birthday?.message}
							label={formPlaceholders.birthday}
							$fieldName="birthday"
						/>
					</Stack>
					{showAPIError && errorStatusCode === HttpStatusCode.unprocessable ? (
						<Alert severity="error">{invalidData}</Alert>
					) : null}
					<Divider />
					<TermsOfUseAndPrivacyPolicy forgotPassword />
					<StyledFixedFooter>
						<Button
							fullWidth
							size="large"
							disabled={isSubmitting}
							type="submit"
							endIcon={isSubmitting ? <Loading color="inherit" size={16} /> : null}>
							{actionText.recoveryPassword}
						</Button>
					</StyledFixedFooter>
					<CompanionAlert />
				</Stack>
			</FormProvider>

			<AlertModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					clearAPIError();
				}}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={unexpectedErrorWarning}
				buttonLabel={tryAgain}
				variant="info"
			/>

			<AlertModal
				footerContent={
					<AlertActions
						flexDirection="column-reverse"
						primaryAction={() => {
							localStorage.setItem(
								LOCAL_STORAGE_KEYS.CAME_FROM_NO_CONTACT_MODAL,
								'true',
							);
							handleHelpStep();
						}}
						secondaryAction={() => navigate(SignInEnum.SIGN_IN)}
						secondaryText={noDataModal.goToHome}
						primaryText={noDataModal.needHelp}
					/>
				}
				subDescription={
					<Typography component="span" textAlign="center">
						{noDataModal.description}
					</Typography>
				}
				headerTitle={unexpectedErrorWarning}
				title={noDataModal.title}
				open={showNoContactModal}
				variant="info"
				disableClose
			/>
		</Fragment>
	);
}
